import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PART_CATEGORIES, PART_CATEGORY_LABELS, type PartCategory } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SearchField } from "@/components/app/patterns/search-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { Toggle } from "@/components/ui/toggle";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, optionalTrue, KEEP_PLACE, searchPatch, optionalSearch } from "@/lib/search";
import { AlertTriangle, Plus } from "@/lib/icons";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { orpc } from "@/lib/orpc";
import { guardFeature } from "@/lib/policy/route-guard";
import { usePaging } from "@/routes/_authenticated/menus/-lib/use-paging";
import { PartForm } from "./-components/part-form";
import { PartsTable } from "./-components/parts-table";
import { useMaintenanceBuilding } from "./-lib/use-maintenance-building";

export const Route = createFileRoute("/_authenticated/_maintenance/maintenance/parts")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; category?: PartCategory; low?: true } => ({
    q: optionalSearch(search.q),
    category: optionalOneOf(PART_CATEGORIES, search.category),
    low: optionalTrue(search.low),
  }),
  beforeLoad: ({ context }) => guardFeature(context.me, "maintenance-shop"),
  component: PartsPage,
});

/** The maintenance shop: what the building keeps, how many, and what runs low. */
function PartsPage() {
  const { facilityId, canManage } = useMaintenanceBuilding();
  const { q, category: pickedCategory, low: pickedLow } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const category: PartCategory | "" = pickedCategory ?? "";
  const setCategory = (next: PartCategory | "") => patch({ category: next || undefined });
  const low = Boolean(pickedLow);
  const setLow = (next: boolean) => patch({ low: next ? true : undefined });
  const [adding, setAdding] = useState(false);
  const paging = usePaging(100);
  const parts = useQuery({
    ...orpc.agents.maintenance.parts.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(q ? { search: q } : {}),
        ...(category ? { category } : {}),
        ...(low ? { low } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || parts.isPlaceholderData;
  const filtered = Boolean(search || category || low);
  const clear = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
    paging.reset();
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          className="max-w-xs sm:w-full"
          value={search}
          onChange={(next) => {
            setSearch(next);
            paging.reset();
          }}
          placeholder="Search the shop"
        />
        <OptionSelect
          aria-label="Category"
          value={category}
          className="w-48"
          onChange={(event) => {
            setCategory(event.target.value as PartCategory | "");
            paging.reset();
          }}
        >
          <SelectOption value="">Every category</SelectOption>
          {PART_CATEGORIES.map((key) => (
            <SelectOption key={key} value={key}>
              {PART_CATEGORY_LABELS[key]}
            </SelectOption>
          ))}
        </OptionSelect>
        <Toggle
          variant="outline"
          size="sm"
          pressed={low}
          onPressedChange={(next) => {
            setLow(next);
            paging.reset();
          }}
        >
          <AlertTriangle className="size-3.5" aria-hidden />
          Low only
          {parts.data?.lowCount ? (
            <span className="text-faint tabular-nums">{String(parts.data.lowCount)}</span>
          ) : null}
        </Toggle>
        {canManage ? (
          <div className="ml-auto">
            <Dialog open={adding} onOpenChange={setAdding}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="size-3.5" />
                  Add part
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add a part to the shop</DialogTitle>
                  <DialogDescription>
                    Give it a minimum and it goes on the list to buy when it runs low.
                  </DialogDescription>
                </DialogHeader>
                <PartForm facilityId={facilityId} start={{}} onSaved={() => setAdding(false)} />
              </DialogContent>
            </Dialog>
          </div>
        ) : null}
      </div>

      {parts.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : parts.isError ? (
        <ReadFailed
          message="Couldn't load the shop."
          error={parts.error}
          onRetry={() => void parts.refetch()}
        />
      ) : parts.data.rows.length === 0 && searching ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : parts.data.rows.length === 0 ? (
        <EmptyState
          title={filtered ? "No part matches" : "The shop is empty"}
          description={
            filtered
              ? "Check the spelling, or clear the filters."
              : "Add the parts the building keeps, filters, capacitors, hardware, or tell David what is on the shelf. A part with a minimum is asked for when it runs low."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <PartsTable
          facilityId={facilityId}
          canManage={canManage}
          parts={parts.data.rows}
          paging={paging}
          total={parts.data.total}
          busy={searching}
        />
      )}
    </section>
  );
}
